import { DEFAULT_LOCALE_ID, ROUTE_CATEGORY_ARTICLE } from '@portfolio/configs'
import {
  MediaFieldType,
  PageBuilderSection,
  PageResponse,
  Seo,
} from '@portfolio/models'
import { getLocaleRegionIdFromPath } from '@portfolio/utils'
import { groq } from 'next-sanity'
import { getClient } from './getClient'
import { coalesceLocaleField } from './helpers/coalesceLocaleField'
import { createArticleTagsProjection } from './projections/createArticleTagsProjection'
import { createMediaProjection } from './projections/createMediaProjection'
import { createSEOProjection } from './projections/createSEOProjection'
import { createTagsProjection } from './projections/createTagsProjection'

type ArticleCategoryPageResult = {
  title?: string
  seo?: Seo
  tags?: Array<{ title: string; path: string; _id: string }>
  articles?: Array<{
    thumbnail?: MediaFieldType
    title?: string
    path?: string
    tags?: Array<{ title: string; path: string; _id: string }>
  }>
}

export const getArticleCategoryPage = async ({
  categorySlug,
  locale,
  preview = false,
}: {
  categorySlug: string
  locale?: string
  preview?: boolean
}): Promise<PageResponse<PageBuilderSection> | null> => {
  const [, localeId] = getLocaleRegionIdFromPath(locale)
  const TITLE_COALESCE = `coalesce(title.${localeId}, title.${DEFAULT_LOCALE_ID})`
  const MEDIA_PROJECTION = createMediaProjection(localeId)
  const ARTICLE_TAGS_PROJECTION = createArticleTagsProjection(localeId)
  const TAGS_PROJECTION = createTagsProjection(localeId)
  const SEO_PROJECTION = createSEOProjection(localeId)

  const query = groq`*[
    _type == 'articleTag' &&
    path.current == "${ROUTE_CATEGORY_ARTICLE}/" + $categorySlug &&
    !(_id in path('drafts.**'))
  ][0]{
    ${coalesceLocaleField('title', localeId)},
    "seo": {
      ...seo${SEO_PROJECTION},
      "metaTitle": coalesce(
        seo.metaTitle.${localeId},
        seo.metaTitle.${DEFAULT_LOCALE_ID},
        ${TITLE_COALESCE}
      ),
      "metaDescription": coalesce(
        seo.metaDescription.${localeId},
        seo.metaDescription.${DEFAULT_LOCALE_ID},
        "Insights in category: " + ${TITLE_COALESCE}
      ),
      "ogTitle": coalesce(
        seo.ogTitle.${localeId},
        seo.ogTitle.${DEFAULT_LOCALE_ID},
        seo.metaTitle.${localeId},
        seo.metaTitle.${DEFAULT_LOCALE_ID},
        ${TITLE_COALESCE}
      ),
      "ogDescription": coalesce(
        seo.ogDescription.${localeId},
        seo.ogDescription.${DEFAULT_LOCALE_ID},
        seo.metaDescription.${localeId},
        seo.metaDescription.${DEFAULT_LOCALE_ID},
        "Insights in category: " + ${TITLE_COALESCE}
      )
    },
    "tags": ${ARTICLE_TAGS_PROJECTION},
    "articles": *[
      _type == 'article' &&
      !(_id in path('drafts.**')) &&
      defined(path.current) &&
      references(^._id)
    ] | order(_createdAt desc) {
      thumbnail${MEDIA_PROJECTION},
      ${coalesceLocaleField('title', localeId)},
      "path": path.current,
      tags[]->${TAGS_PROJECTION},
    }
  }`

  const categoryPage = await getClient(
    preview,
  ).fetch<ArticleCategoryPageResult | null>(query, {
    categorySlug,
  })

  if (!categoryPage?.articles?.length) {
    return null
  }

  return {
    type: 'articlesLandingPage',
    path: `${ROUTE_CATEGORY_ARTICLE}/${categorySlug}`,
    showFooter: true,
    headerStyle: 'transparent-dark',
    footerMobileButtonVisible: false,
    seo: categoryPage.seo,
    pageBuilder: [
      {
        _type: 'sectionArticlesLandingPage',
        _key: `sectionArticlesLandingPage-${categorySlug}`,
        title: `${categoryPage.title} Insights`,
        tags: categoryPage.tags,
        articles: categoryPage.articles,
      },
    ],
  }
}
